import { ApolloError, from } from '@apollo/client';
import { InMemoryCache } from '@apollo/client';
import { ApolloClient, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient as createWsClient } from 'graphql-ws';
import { getApiBaseUrl } from '@oysterjs/core/config';
import { getValidationErrors } from './errors';
import { mergeEdgesConnection, mergeUniqueEdgesConnection } from './edges-connection';
import { GraphQLFormattedError, Kind, OperationTypeNode } from 'graphql';

/**
 * Relay-style merge for a connection whose page is `{ nodes, pageInfo, totalCount }`.
 * Appends incoming nodes when paginating (an `after` cursor is present) and replaces
 * the page wholesale otherwise (first page / refetch). Mirrors the `searchCaraWorkspaces`
 * policy below but for `nodes` rather than `edges`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mergeNodesConnection = (
  existing: any,
  incoming: any,
  options: { args?: Record<string, any> | null }
) => {
  const after =
    options && options.args && typeof options.args.after !== 'undefined'
      ? options.args.after
      : undefined;
  if (!incoming) return existing;
  if (!existing || !after) return incoming;
  return {
    ...incoming,
    nodes: [...(existing.nodes ?? []), ...(incoming.nodes ?? [])],
    pageInfo: incoming.pageInfo,
    totalCount: incoming.totalCount
  };
};

/**
 * The cache every app runs on, factored out so a test watches the same paging
 * policies an app does rather than a bare InMemoryCache.
 */
export const createInMemoryCache = () =>
  new InMemoryCache({
    typePolicies: {
      CaraWorkspaceArtifact: {
        keyFields: ['id', 'version'],
        fields: {
          // Two operations write this field at the same key and one of them
          // knows less: the workspace subscription sends an email without its
          // body -- pushing one per version per tick is what the split avoids
          // -- while the composer's fetch and its saves send the body too.
          // Apollo replaces a nested object it cannot normalise, and
          // CaraWorkspaceArtifactEmail has no id to normalise on, so the
          // default would let a tick erase the words a composer is displaying.
          // Merging keeps them; recipients and subject still move, because the
          // incoming write names them and the merge is field-by-field.
          email: { merge: true }
        }
      },
      Client: {
        fields: {
          // Paginated connections, so they need the same treatment as the
          // Query-level ones below: `after` out of the key, and a merge that
          // appends. Without a policy Apollo files each page under its own
          // field key and fetchMore leaves the rendered page untouched.
          artifacts: {
            keyArgs: false,
            merge: mergeEdgesConnection
          },
          files: {
            keyArgs: false,
            merge: mergeEdgesConnection
          }
        }
      },
      Query: {
        fields: {
          // Everything but the cursor, like searchCalls below: several pages read
          // this field, and a shared slot lets a picker's 10 rows overwrite a
          // list's 25. `after` stays out so fetchMore still merges.
          searchCaraWorkspaces: {
            keyArgs: ['first', 'filter', 'sort'],
            merge: mergeEdgesConnection
          },
          // Per-policy lazy loads, loose-only, and account-wide searches must not
          // collide, so the key includes every scoping arg except the cursor/limit.
          amsAccountDocuments: {
            keyArgs: ['accountId', 'policyId', 'looseFilesOnly', 'query'],
            merge: mergeNodesConnection
          },
          searchCalls: {
            keyArgs: ['first', 'filter', 'sort'],
            merge: mergeUniqueEdgesConnection
          },
          searchOutboundCalls: {
            keyArgs: ['filter', 'sort'],
            merge: mergeUniqueEdgesConnection
          },
          searchOutboundEmails: {
            keyArgs: ['filter', 'sort'],
            merge: mergeUniqueEdgesConnection
          },
          searchMailboxDrafts: {
            keyArgs: ['filter'],
            merge: mergeUniqueEdgesConnection
          },
          searchCaraWorkspaceArtifacts: {
            keyArgs: ['filter'],
            merge: mergeUniqueEdgesConnection
          },
          searchMemories: {
            keyArgs: ['filter'],
            merge: mergeUniqueEdgesConnection
          },
          // Without this, fetchMore writes each page under its own cursor-keyed
          // field that the watched query never reads, so "Load more" fetches a
          // page and displays nothing. Only `after` is omitted, so the Clients
          // list and the smaller client pickers keep separate slots.
          clients: {
            keyArgs: ['first', 'filter', 'sort'],
            merge: mergeEdgesConnection
          },
          // Only one surface reads this, so the slot is keyed on the arguments
          // that change what it holds. `after` is omitted, as above, so
          // "Load more" appends to the page already on screen.
          clientMergeCandidates: {
            keyArgs: ['first', 'status'],
            merge: mergeEdgesConnection
          }
        }
      }
    }
  });

export const createClient = (namespace: 'Agency' | 'Admin' | 'Clients') =>
  new ApolloClient({
    cache: createInMemoryCache(),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      },
      mutate: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      }
    },
    link: from([
      removeTypenameFromVariables(),
      split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        new GraphQLWsLink(
          createWsClient({
            url: `${getApiBaseUrl()}/graphql/query`,
            connectionParams: {
              'X-Oyster-Application-Namespace': namespace
            }
          })
        ),
        createUploadLink({
          uri: `${getApiBaseUrl()}/graphql/query`,
          credentials: 'include',
          fetch: async (uri: string | URL, options: RequestInit) => {
            // Set the app namespace in the headers
            options.headers = {
              ...options.headers,
              'X-Oyster-Application-Namespace': namespace
            };

            // Send the request as usual to the server
            const response = await fetch(uri, options);

            // In most cases, we will want to return the response as usual. However, GraphQL will
            // return a status 422 or 400 if there is a validation error before the resolver is called. In
            // some of these cases, the server is configured to return a JSON response with the
            // validation error. If we detect this case, we actually want to continue with the request
            // as normal, so we need to modify the response's status code to 200.
            if (response.status === 422 || response.status === 400) {
              // Check the body for a validation error
              const cloned = response.clone();
              const body = await cloned.json();

              const graphQLErrors: GraphQLFormattedError[] | undefined = body.errors;

              if (graphQLErrors && !!getValidationErrors(new ApolloError({ graphQLErrors }))) {
                // If there are validation errors, modify the response's status code to 200
                return new Response(JSON.stringify(body), {
                  status: 200,
                  statusText: 'OK',
                  headers: response.headers
                });
              }
            }

            return response;
          }
        })
      )
    ])
  });
